/**
 * ============================================================================
 * App监控日志 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 App 监控日志模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 📦 包含的功能：
 * - 性能日志查询和统计（Web 管理后台使用）
 * - 错误日志查询和统计（Web 管理后台使用）
 * - 用户行为日志查询和统计（Web 管理后台使用）
 * - API 分析（全局/用户/设备维度）
 * - 用户分析（列表、画像、时间线）
 * - 设备分析（列表、画像）
 * - 告警管理
 * - 漏斗分析
 * - 批量上传接口（App 端使用，免鉴权）
 * 
 * @module api/app-logs/factory
 * @author YSFL Team
 * @created 2026-01-21
 * @updated 2026-01-31 添加用户/设备分析、统计接口
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  AppPerformanceLog,
  AppErrorLog,
  AppUserJourneyLog,
  AppLogUploadResponse,
  AppPerformanceLogQueryParams,
  AppErrorLogQueryParams,
  AppUserJourneyLogQueryParams,
  BatchUploadPerformanceLogsForm,
  BatchUploadErrorLogsForm,
  BatchUploadJourneyLogsForm,
  // 统计类型
  PerformanceStatistics,
  ErrorStatistics,
  JourneyStatistics,
  StatisticsQueryParams,
  // API 分析类型
  ApiStatistics,
  ApiStatisticsQueryParams,
  ApiDetail,
  ApiDetailQueryParams,
  AppLogsHealth,
  AppLogsListResult,
  AppLogTraceQueryParams,
  AppLogTraceResult,
  // 用户分析类型
  UserListItem,
  UserListQueryParams,
  UserProfile,
  TimelineEvent,
  TimelineQueryParams,
  // 设备分析类型
  DeviceListItem,
  DeviceListQueryParams,
  DeviceProfile,
  // 告警类型
  AppMonitoringAlert,
  AlertSummary,
  AlertStatus,
  AlertType,
  AlertLevel,
  AlertDimensionType,
  // 漏斗类型
  FunnelDefinition,
  FunnelAnalysis,
  InterruptionAnalysis,
  ScannerPerformanceStatistics,
} from '../../types/app-logs'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入请求客户端接口 =====
import type { IRequestClient } from '../core/client'

// ===== 导入路由定义 =====
import { APP_LOGS_ROUTES } from './routes'

// ===== 导出工厂函数 =====

/**
 * 创建 App 监控日志模块的 API 方法集合
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 * @returns API 方法集合对象
 * 
 * @example
 * ```typescript
 * // Web 端使用
 * import { createAppLogsApi } from '@bayes-mes/shared/api/app-logs'
 * const appLogsApi = createAppLogsApi(requestClient)
 * 
 * // 查询性能日志
 * const logs = await appLogsApi.getPerformanceLogs({ page: 1, per_page: 20 })
 * 
 * // 获取性能统计
 * const stats = await appLogsApi.getPerformanceStatistics({ start_date: '2026-01-01' })
 * ```
 */
export function createAppLogsApi(client: IRequestClient) {
  return {
    // =========================================================================
    // 性能日志
    // =========================================================================

    /**
     * 查询性能日志列表（分页）
     *
     * 使用场景：
     * - Web 管理后台的性能日志列表页面
     * - 支持按设备、平台、指标类型、时间范围等筛选
     *
     * @param params - 查询参数（snake_case）
     * @returns Promise<PaginationResult<AppPerformanceLog>> 分页结果
     */
    getPerformanceLogs(params?: AppPerformanceLogQueryParams) {
      return client.get<PaginationResult<AppPerformanceLog>>(
        APP_LOGS_ROUTES.performance.list,
        { params }
      )
    },

    /**
     * 获取性能统计聚合数据
     *
     * 使用场景：
     * - 仪表盘性能概览
     * - 性能趋势分析
     *
     * @param params - 查询参数
     * @returns Promise<PerformanceStatistics> 性能统计数据
     */
    getPerformanceStatistics(params?: StatisticsQueryParams) {
      return client.get<PerformanceStatistics>(
        APP_LOGS_ROUTES.performance.statistics,
        { params }
      )
    },

    getScannerPerformanceStatistics(params?: StatisticsQueryParams) {
      return client.get<ScannerPerformanceStatistics>(
        APP_LOGS_ROUTES.performance.scannerStatistics,
        { params },
      )
    },

    /**
     * 批量上传性能日志（App 端使用，免鉴权）
     *
     * 使用场景：
     * - App 端 SDK 批量上报性能数据
     * - 支持离线缓存后批量上传
     *
     * @param data - 批量上传表单数据
     * @returns Promise<AppLogUploadResponse> 上传结果
     */
    batchUploadPerformanceLogs(data: BatchUploadPerformanceLogsForm) {
      return client.post<AppLogUploadResponse>(
        APP_LOGS_ROUTES.performance.batch,
        data
      )
    },

    // =========================================================================
    // 错误日志
    // =========================================================================

    /**
     * 查询错误日志列表（分页）
     * 
     * 使用场景：
     * - Web 管理后台的错误日志列表页面
     * - 支持按设备、平台、错误类型、错误级别、时间范围等筛选
     * 
     * @param params - 查询参数（snake_case）
     * @returns Promise<PaginationResult<AppErrorLog>> 分页结果
     */
    getErrorLogs(params?: AppErrorLogQueryParams) {
      return client.get<PaginationResult<AppErrorLog>>(
        APP_LOGS_ROUTES.errors.list,
        { params }
      )
    },

    /**
     * 获取错误统计聚合数据
     * 
     * 使用场景：
     * - 仪表盘错误概览
     * - 错误趋势分析
     * 
     * @param params - 查询参数
     * @returns Promise<ErrorStatistics> 错误统计数据
     */
    getErrorStatistics(params?: StatisticsQueryParams) {
      return client.get<ErrorStatistics>(
        APP_LOGS_ROUTES.errors.statistics,
        { params }
      )
    },

    /**
     * 批量上传错误日志（App 端使用，免鉴权）
     * 
     * 使用场景：
     * - App 端 SDK 批量上报错误数据
     * - 支持捕获 JS 错误、API 错误、业务错误等
     * 
     * @param data - 批量上传表单数据
     * @returns Promise<AppLogUploadResponse> 上传结果
     */
    batchUploadErrorLogs(data: BatchUploadErrorLogsForm) {
      return client.post<AppLogUploadResponse>(
        APP_LOGS_ROUTES.errors.batch,
        data
      )
    },

    // =========================================================================
    // 用户行为日志
    // =========================================================================

    /**
     * 查询用户行为日志列表（分页）
     * 
     * 使用场景：
     * - Web 管理后台的用户行为日志列表页面
     * - 支持按设备、平台、操作类型、操作结果、时间范围等筛选
     * 
     * @param params - 查询参数（snake_case）
     * @returns Promise<PaginationResult<AppUserJourneyLog>> 分页结果
     */
    getJourneyLogs(params?: AppUserJourneyLogQueryParams) {
      return client.get<PaginationResult<AppUserJourneyLog>>(
        APP_LOGS_ROUTES.journey.list,
        { params }
      )
    },

    /**
     * 获取行为统计聚合数据
     * 
     * 使用场景：
     * - 仪表盘行为概览
     * - 用户行为分析
     * 
     * @param params - 查询参数
     * @returns Promise<JourneyStatistics> 行为统计数据
     */
    getJourneyStatistics(params?: StatisticsQueryParams) {
      return client.get<JourneyStatistics>(
        APP_LOGS_ROUTES.journey.statistics,
        { params }
      )
    },

    /**
     * 批量上传用户行为日志（App 端使用，免鉴权）
     * 
     * 使用场景：
     * - App 端 SDK 批量上报用户行为数据
     * - 记录页面访问、按钮点击、表单提交等操作
     * 
     * @param data - 批量上传表单数据
     * @returns Promise<AppLogUploadResponse> 上传结果
     */
    batchUploadJourneyLogs(data: BatchUploadJourneyLogsForm) {
      return client.post<AppLogUploadResponse>(
        APP_LOGS_ROUTES.journey.batch,
        data
      )
    },

    // =========================================================================
    // API 分析
    // =========================================================================

    /**
     * 获取 App 日志上报健康状态
     * 
     * 使用场景：
     * - 运维查看 Redis 队列积压
     * - 判断 App 日志是否仍在上报、是否完成入库
     * 
     * @returns Promise<AppLogsHealth> 上报健康状态
     */
    getHealthStatus() {
      return client.get<AppLogsHealth>(
        APP_LOGS_ROUTES.api.health
      )
    },

    /**
     * 按排障线索追踪 App 日志事件
     *
     * 使用场景：
     * - 运维输入 requestId/supportCode 快速串联性能、错误和行为日志
     * - 现场反馈设备、用户或 API 异常后回放问题过程
     *
     * @param params - 查询参数
     * @returns Promise<AppLogTraceResult> 追踪事件和派生线索
     */
    getTraceEvents(params: AppLogTraceQueryParams) {
      return client.get<AppLogTraceResult>(
        APP_LOGS_ROUTES.api.traces,
        { params }
      )
    },

    /**
     * 获取 API 统计分析
     * 
     * 使用场景：
     * - API 分析页面
     * - 支持全局/用户/设备维度
     * 
     * @param params - 查询参数
     * @returns Promise<ApiStatistics> API 统计数据
     */
    getApiStatistics(params?: ApiStatisticsQueryParams) {
      return client.get<ApiStatistics>(
        APP_LOGS_ROUTES.api.statistics,
        { params }
      )
    },

    /**
     * 获取单个 API 详情分析
     * 
     * 使用场景：
     * - API 详情页面
     * - 分析单个 API 的调用情况
     * 
     * @param params - 查询参数
     * @returns Promise<ApiDetail> API 详情数据
     */
    getApiDetail(params: ApiDetailQueryParams) {
      return client.get<ApiDetail>(
        APP_LOGS_ROUTES.api.detail,
        { params }
      )
    },

    // =========================================================================
    // 用户分析
    // =========================================================================

    /**
     * 获取用户列表
     * 
     * 使用场景：
     * - 用户分析页面
     * - 查看活跃用户列表
     * 
     * @param params - 查询参数
     * @returns Promise<AppLogsListResult<UserListItem>> 用户列表
     */
    getUserList(params?: UserListQueryParams) {
      return client.get<AppLogsListResult<UserListItem>>(
        APP_LOGS_ROUTES.users.list,
        { params }
      )
    },

    /**
     * 获取用户画像
     * 
     * 使用场景：
     * - 用户详情页面
     * - 分析单个用户的行为和性能数据
     * 
     * @param userId - 用户ID
     * @param params - 查询参数
     * @returns Promise<UserProfile> 用户画像数据
     */
    getUserProfile(userId: string | number, params?: { start_date?: string; end_date?: string }) {
      return client.get<UserProfile>(
        APP_LOGS_ROUTES.users.profile(userId),
        { params }
      )
    },

    /**
     * 获取用户时间线
     * 
     * 使用场景：
     * - 用户详情页面的时间线 Tab
     * - 查看用户的操作事件时间线
     * 
     * @param userId - 用户ID
     * @param params - 查询参数
     * @returns Promise<AppLogsListResult<TimelineEvent>> 时间线数据
     */
    getUserTimeline(userId: string | number, params?: TimelineQueryParams) {
      return client.get<AppLogsListResult<TimelineEvent>>(
        APP_LOGS_ROUTES.users.timeline(userId),
        { params }
      )
    },

    // =========================================================================
    // 设备分析
    // =========================================================================

    /**
     * 获取设备列表
     * 
     * 使用场景：
     * - 设备分析页面
     * - 查看活跃设备列表
     * 
     * @param params - 查询参数
     * @returns Promise<AppLogsListResult<DeviceListItem>> 设备列表
     */
    getDeviceList(params?: DeviceListQueryParams) {
      return client.get<AppLogsListResult<DeviceListItem>>(
        APP_LOGS_ROUTES.devices.list,
        { params }
      )
    },

    /**
     * 获取设备画像
     * 
     * 使用场景：
     * - 设备详情页面
     * - 分析单个设备的性能和错误数据
     * 
     * @param deviceId - 设备ID
     * @param params - 查询参数
     * @returns Promise<DeviceProfile> 设备画像数据
     */
    getDeviceProfile(deviceId: string | number, params?: { start_date?: string; end_date?: string }) {
      return client.get<DeviceProfile>(
        APP_LOGS_ROUTES.devices.profile(deviceId),
        { params }
      )
    },

    /**
     * 获取设备时间线
     * 
     * 使用场景：
     * - 设备详情页面的时间线 Tab
     * - 按设备回放性能、错误和行为事件
     * 
     * @param deviceId - 设备ID
     * @param params - 查询参数
     * @returns Promise<AppLogsListResult<TimelineEvent>> 时间线数据
     */
    getDeviceTimeline(deviceId: string | number, params?: TimelineQueryParams) {
      return client.get<AppLogsListResult<TimelineEvent>>(
        APP_LOGS_ROUTES.devices.timeline(deviceId),
        { params }
      )
    },

    // =========================================================================
    // 告警管理
    // =========================================================================

    /**
     * 获取告警列表（分页）
     * 
     * 使用场景：
     * - Web 管理后台的告警中心页面
     * - 支持按状态、类型、级别、维度等筛选
     * 
     * @param params - 查询参数（snake_case）
     * @returns Promise<PaginationResult<AppMonitoringAlert>> 分页结果
     */
    getAlerts(params?: {
      status?: AlertStatus
      alert_type?: AlertType
      alert_level?: AlertLevel
      dimension_type?: AlertDimensionType
      dimension_value?: string
      start_date?: string
      end_date?: string
      page?: number
      per_page?: number
    }) {
      return client.get<PaginationResult<AppMonitoringAlert>>(
        APP_LOGS_ROUTES.alerts.list,
        { params }
      )
    },

    /**
     * 获取告警统计概览
     * 
     * 使用场景：
     * - 告警中心页面的统计卡片
     * - 显示各级别告警数量和今日统计
     * 
     * @returns Promise<AlertSummary> 告警统计数据
     */
    getAlertSummary() {
      return client.get<AlertSummary>(APP_LOGS_ROUTES.alerts.summary)
    },

    /**
     * 获取告警详情
     * 
     * @param id - 告警ID
     * @returns Promise<AppMonitoringAlert> 告警详情
     */
    getAlertDetail(id: number) {
      return client.get<AppMonitoringAlert>(APP_LOGS_ROUTES.alerts.detail(id))
    },

    /**
     * 处理告警
     * 
     * 使用场景：
     * - 确认、解决或忽略单个告警
     * 
     * @param id - 告警ID
     * @param data - 处理数据
     * @returns Promise<AppMonitoringAlert> 更新后的告警
     */
    handleAlert(id: number, data: { action: 'confirm' | 'resolve' | 'ignore'; note?: string }) {
      return client.post<AppMonitoringAlert>(APP_LOGS_ROUTES.alerts.handle(id), data)
    },

    /**
     * 批量处理告警
     * 
     * 使用场景：
     * - 批量确认、解决或忽略多个告警
     * 
     * @param data - 批量处理数据
     * @returns Promise<{ total: number; success: number; failed: number }> 处理结果
     */
    batchHandleAlerts(data: { ids: number[]; action: 'confirm' | 'resolve' | 'ignore'; note?: string }) {
      return client.post<{ total: number; success: number; failed: number }>(
        APP_LOGS_ROUTES.alerts.batchHandle,
        data
      )
    },

    /**
     * 手动触发告警检测
     * 
     * 使用场景：
     * - 立即执行告警检测，不等待定时任务
     * 
     * @returns Promise<{ newAlerts: number; autoResolved: number }> 检测结果
     */
    triggerAlertDetection() {
      return client.post<{ newAlerts: number; autoResolved: number }>(APP_LOGS_ROUTES.alerts.detect)
    },

    // =========================================================================
    // 漏斗分析
    // =========================================================================

    /**
     * 获取漏斗定义列表
     * 
     * 使用场景：
     * - 流程分析页面的漏斗选择器
     * 
     * @returns Promise<FunnelDefinition[]> 漏斗定义列表
     */
    getFunnelList() {
      return client.get<FunnelDefinition[]>(APP_LOGS_ROUTES.funnel.list)
    },

    /**
     * 获取漏斗分析数据
     * 
     * 使用场景：
     * - 流程分析页面的漏斗可视化
     * - 支持按用户/设备维度下钻
     * 
     * @param params - 查询参数（snake_case）
     * @returns Promise<FunnelAnalysis> 漏斗分析数据
     */
    getFunnelAnalysis(params: {
      funnel_code: string
      start_date?: string
      end_date?: string
      dimension?: 'global' | 'user' | 'device'
      dimension_value?: string
    }) {
      return client.get<FunnelAnalysis>(APP_LOGS_ROUTES.funnel.analysis, { params })
    },

    /**
     * 获取操作中断分析
     * 
     * 使用场景：
     * - 流程分析页面的中断分析 Tab
     * - 分析用户操作失败和中断情况
     * 
     * @param params - 查询参数（snake_case）
     * @returns Promise<InterruptionAnalysis> 中断分析数据
     */
    getInterruptions(params?: {
      module?: string
      start_date?: string
      end_date?: string
      dimension?: 'global' | 'user' | 'device'
      dimension_value?: string
    }) {
      return client.get<InterruptionAnalysis>(APP_LOGS_ROUTES.funnel.interruptions, { params })
    },

    /**
     * 创建漏斗定义
     * 
     * @param data - 漏斗定义数据
     * @returns Promise<FunnelDefinition> 创建的漏斗定义
     */
    createFunnel(data: {
      name: string
      code: string
      description?: string
      module: string
      steps: Array<{
        order: number
        name: string
        action_name: string
        page_url?: string
      }>
    }) {
      return client.post<FunnelDefinition>(APP_LOGS_ROUTES.funnel.create, data)
    },

    /**
     * 更新漏斗定义
     * 
     * @param id - 漏斗ID
     * @param data - 更新数据
     * @returns Promise<FunnelDefinition> 更新后的漏斗定义
     */
    updateFunnel(id: number, data: {
      name?: string
      description?: string
      module?: string
      steps?: Array<{
        order: number
        name: string
        action_name: string
        page_url?: string
      }>
      enabled?: boolean
    }) {
      return client.put<FunnelDefinition>(APP_LOGS_ROUTES.funnel.update(id), data)
    },

    /**
     * 删除漏斗定义
     * 
     * @param id - 漏斗ID
     * @returns Promise<void>
     */
    deleteFunnel(id: number) {
      return client.delete<void>(APP_LOGS_ROUTES.funnel.delete(id))
    },
  }
}
